import { cancelObjectAssetPlacement } from '../objects/object_asset_system.js?v=20260903c';
import * as AppController from '../app/app_controller.js?v=20260904f';
import * as SelectionController from '../selection/selection_controller.js?v=20260904f';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  OBSTACLE_TYPES,
  cancelGlobalObstacleDrawMode,
  clearObstacleSelection,
  deleteObstacle,
  deleteSelectedObstacles,
  moveObstacle,
  obstacleActiveTool,
  selectedObstacles
} from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';
import { runProjectMutation } from '../project/project_mutation_transaction.js';
import { clearDrawingSelection } from '../selection/drawing_selection_manager.js?v=20260706d';
import {
  recordUndo,
  redoLast,
  undoLast
} from '../services/history_service.js?v=20260903c';
import {
  rotateDrawingObjects
} from './drawing_transform_controller.js?v=20260904f';
import {
  cancelDimensionInteraction,
  deleteSelectedDimension
} from '../services/dimension_service.js?v=20260904f';
import {
  cancelTextAnnotationInteraction,
  deleteSelectedTextAnnotation,
  nudgeSelectedTextAnnotation
} from '../services/text_annotation_service.js?v=20260903c';
import {
  cancelAreaMeasurementInteraction,
  deleteSelectedAreaMeasurement
} from '../services/area_measurement_service.js?v=20260903c';
import {
  cancelAutoAreaMeasurementInteraction
} from '../services/auto_area_measurement_service.js?v=20260814c';

const GLOBAL_KEY_CODES = new Set([
  'Escape',
  'Delete',
  'Backspace',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
  'KeyZ',
  'KeyY',
  'KeyC',
  'KeyV',
  'KeyR'
]);

function isTypingTarget(target) {
  const tag = target?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'select' || tag === 'textarea' || target?.isContentEditable;
}

// .modal-backdrop ist die gemeinsame Wurzelklasse ALLER Modale in dieser App
// (Drucken, Dossier, Artikelsuche, ks-modal-basierte Admin-Modale, ...).
// Ohne diese Prüfung feuerte dieser globale window-keydown-Handler (siehe
// interaction_controller.js) unverändert weiter, WÄHREND ein Modal offen ist
// — z.B. löschte Entf/Backspace im geöffneten Drucken-Modal (dort noch ohne
// eigene Tastatur-Behandlung für die 2D-Ansichtsfenster) das im Hintergrund
// selektierte Zeichnungsobjekt statt gar nichts zu tun.
// Betrifft nicht nur Löschen, sondern auch Undo/Redo/Rotate/Pfeiltasten-
// Verschieben — ein offenes Modal muss den kompletten globalen
// Zeichnungs-Tastaturkanal stummschalten, das jeweilige Modal behandelt
// seine eigenen Tasten (z.B. Escape) selbst.
function isModalOpen() {
  return Boolean(document.querySelector('.modal-backdrop'));
}

function shouldHandleGlobalKeyDown(event) {
  if (!event) return false;
  if (isModalOpen()) return false;
  if (obstacleActiveTool() && S.ui?.obstacleDraw?.startPoint) return true;
  if (event.ctrlKey || event.metaKey) {
    return ['z', 'y', 'c', 'v'].includes(String(event.key || '').toLowerCase())
      || ['KeyZ', 'KeyY', 'KeyC', 'KeyV'].includes(event.code);
  }
  return GLOBAL_KEY_CODES.has(event.code) || event.key === 'Escape';
}

function handleUndoRedoKey(e) {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;
  if (isTypingTarget(e.target)) return false;

  const key = String(e.key || '').toLowerCase();
  if (key === 'z' && !e.shiftKey) {
    e.preventDefault();
    if (undoLast()) AppController.refresh(false);
    return true;
  }

  if ((key === 'z' && e.shiftKey) || key === 'y') {
    e.preventDefault();
    if (redoLast()) AppController.refresh(false);
    return true;
  }

  return false;
}

function handleEscapeKey(e) {
  if (e.key !== 'Escape') return false;
  if (S.project.currentView !== 'plan') return false;

  if (S.ui?.showLevelsPanel) {
    e.preventDefault();
    SelectionController.closeLevelsPanelForToolChange();
    setStatus('Ebenen deaktiviert');
    AppController.refresh(false);
    return true;
  }

  if (cancelObjectAssetPlacement()) { e.preventDefault(); return true; }
  if (SystemRegistry.cancelInteraction(null, { event: e, currentView: S.project.currentView })) return true;

  if (cancelDimensionInteraction()) {
    e.preventDefault();
    AppController.refresh(false);
    return true;
  }

  if (cancelAutoAreaMeasurementInteraction()) {
    e.preventDefault();
    AppController.refresh(false);
    return true;
  }

  if (cancelAreaMeasurementInteraction()) {
    e.preventDefault();
    AppController.refresh(false);
    return true;
  }

  if (cancelTextAnnotationInteraction()) {
    e.preventDefault();
    AppController.refresh(false);
    return true;
  }

  if (obstacleActiveTool()) {
    e.preventDefault();
    cancelGlobalObstacleDrawMode();
    SelectionController.clearGlobalSelection();
    setStatus('Funktion abgebrochen');
    return true;
  }

  if (SelectionController.clearGlobalSelection()) {
    e.preventDefault();
    setStatus('Auswahl aufgehoben');
    AppController.refresh(false);
    return true;
  }

  return false;
}

// Independent owner histories cannot participate in a legacy whole-project
// transaction. Dispatch a single owner; reject mixed writes before any mutation.
function ownerCommandSelection(multiSelection) {
  const items=S.selection?.items || [];
  const selected=SystemRegistry.allSystems().filter(system=>(system.multiSelectionFields || []).some(field=>multiSelection[field]?.length)
    || items.some(item=>item.owner===system.id));
  if(!selected.some(system=>system.selectionCommitMode==='owner'))return null;
  const owner=selected[0];
  if(selected.length!==1 || multiSelection.obstacleIds?.length || items.some(item=>item.owner!==owner.id)) {
    setStatus('Diese Auswahl bitte getrennt nach System bearbeiten.');return {blocked:true};
  }
  return {id:owner.id,blocked:false};
}

function handleDeleteKey(e) {
  if (!['Delete', 'Backspace'].includes(e.code)) return false;
  if (isTypingTarget(e.target)) return false;

  if (deleteSelectedDimension()) {
    e.preventDefault();
    return true;
  }

  if (deleteSelectedAreaMeasurement()) {
    e.preventDefault();
    return true;
  }

  if (deleteSelectedTextAnnotation()) {
    e.preventDefault();
    return true;
  }

  const activeSystemId = SystemRegistry.activeSystem()?.id || null;
  const obstacles = selectedObstacles();
  const multiSelection = SelectionController.resolvedMultiSelection();
  const ownerCommand=ownerCommandSelection(multiSelection);
  if(ownerCommand) {
    e.preventDefault();
    if(!ownerCommand.blocked)SystemRegistry.deleteSelection(ownerCommand.id,{event:e});
    return true;
  }
  // Deletion must not depend on whether an owner supports movement. Child
  // selections (e.g. a door) likewise participate beside movable objects.
  const systemSelectionCount = SystemRegistry.allSystems().reduce((count, system) => count
    + (system.multiSelectionFields || []).reduce((sum, field) => sum + (multiSelection[field]?.length || 0), 0), 0);
  const systemItems = (S.selection?.items || []).filter(item => item?.owner && item.owner !== 'core');
  if (obstacles.length && !systemSelectionCount && !systemItems.length) {
    e.preventDefault();
    if (!runProjectMutation('Hindernis löschen', () => deleteSelectedObstacles())) return true;
    setStatus(obstacles.length > 1 ? `${obstacles.length} Hindernisse gelöscht` : 'Hindernis gelöscht');
    AppController.refresh(false);
    return true;
  }

  const multiCount = systemSelectionCount + multiSelection.obstacleIds.length;
  if (multiCount > 0 || systemItems.length > 1) {
    e.preventDefault();
    const deleteCount = Math.max(multiCount, systemItems.length + multiSelection.obstacleIds.length);
    const deleted = runProjectMutation(deleteCount === 1 ? 'Objekt loeschen' : 'Objekte loeschen', () => {
      let count = SystemRegistry.deleteObjectsForSelection({ selection: multiSelection, items: S.selection?.items }, { requireAll: true });
      if (count === false) return false;
      for (const obstacleId of multiSelection.obstacleIds) {
        if (!deleteObstacle(obstacleId)) return false;
        count += 1;
      }
      if (!count) return false;
      SelectionController.clearMultiSelection();
      clearObstacleSelection();
      SystemRegistry.clearAllSelections();
      return count;
    });
    if (!deleted) return true;
    clearDrawingSelection();
    AppController.refresh(false);
    return true;
  }

  return SystemRegistry.deleteFirstSelection({ event: e, systemId: activeSystemId }) === true;
}

function handleArrowNudge(e) {
  if (S.project.currentView !== 'plan') return false;
  if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) return false;
  if (isTypingTarget(e.target)) return false;

  const numericGridSize = Number(S.project.gridSize);
  const step = Number.isFinite(numericGridSize) ? numericGridSize : 10;
  const dx = e.code === 'ArrowLeft' ? -step : e.code === 'ArrowRight' ? step : 0;
  const dz = e.code === 'ArrowUp' ? -step : e.code === 'ArrowDown' ? step : 0;
  if (nudgeSelectedTextAnnotation(dx, dz)) {
    e.preventDefault();
    return true;
  }
  const multiSelection = SelectionController.resolvedMultiSelection();
  const ownerCommand=ownerCommandSelection(multiSelection);
  if(ownerCommand) {
    e.preventDefault();
    if(!ownerCommand.blocked)SystemRegistry.nudgeSelection(ownerCommand.id,{event:e,dx,dz,multiSelection,step});
    return true;
  }
  const multiMoveSnapshot = SelectionController.buildMultiSelectionMoveSnapshot(multiSelection);
  const multiMoveCount = SelectionController.multiSelectionMoveSnapshotCount(multiMoveSnapshot);

  const activeSystemId = SystemRegistry.activeSystem()?.id || null;
  const obstacles = selectedObstacles();
  const systemMoveCount = SelectionController.multiSelectionMoveSnapshotCount({
    systemSnapshots: multiMoveSnapshot.systemSnapshots || [],
    startObstacles: []
  });
  if (obstacles.length && !systemMoveCount) {
    e.preventDefault();
    const obstacleLabel = obstacles.length > 1
      ? 'Hindernisse'
      : (OBSTACLE_TYPES[obstacles[0].type]?.label || 'Hindernis');
    recordUndo(`${obstacleLabel} verschieben`);
    obstacles.forEach(obstacle => moveObstacle(obstacle.id, dx, dz));
    SelectionService.syncFromLegacy();
    setStatus(obstacles.length > 1 ? `${obstacles.length} Hindernisse verschoben` : `${obstacleLabel} verschoben`);
    AppController.refresh(false);
    return true;
  }
  if (multiMoveCount > 1) {
    e.preventDefault();
    recordUndo('Auswahl verschieben');
    SelectionController.applyMultiSelectionMoveDelta(multiMoveSnapshot, dx, dz);
    SelectionController.syncSelectionAfterMultiMove(multiMoveSnapshot);
    setStatus(`${multiMoveCount} Objekte verschoben`);
    AppController.refresh(false);
    return true;
  }
  return SystemRegistry.nudgeFirstSelection({ event: e, dx, dz, multiSelection, step, systemId: activeSystemId }) === true;
}

function handleRotateKey(e) {
  if (S.project.currentView !== 'plan') return false;
  if (e.code !== 'KeyR' && String(e.key || '').toLowerCase() !== 'r') return false;
  if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return false;

  const multiSelection = SelectionController.resolvedMultiSelection();
  const multiRotationSnapshot = SelectionController.buildMultiSelectionMoveSnapshot(multiSelection);
  const multiRotationCount = SelectionController.multiSelectionMoveSnapshotCount(multiRotationSnapshot);
  // Native systems can expose canonical selection without the legacy drawing-selection bridge.
  // Their normal transform snapshot also owns a single selected object.
  if (multiRotationCount > 0) {
    e.preventDefault();
    recordUndo('Auswahl drehen');
    const rotated = SelectionController.applyMultiSelectionRotation(multiRotationSnapshot, 90);
    if (!rotated) return false;
    setStatus(`${rotated} Objekte um Gruppenmittelpunkt gedreht`);
    AppController.refresh(false);
    return true;
  }

  e.preventDefault();
  recordUndo('Objekt drehen');
  const rotated = rotateDrawingObjects(null, 90) > 0;
  if (!rotated) return false;
  setStatus('Objekt gedreht');
  AppController.refresh(false);
  return true;
}

export {
  isTypingTarget,
  isModalOpen,
  shouldHandleGlobalKeyDown,
  handleUndoRedoKey,
  handleEscapeKey,
  handleDeleteKey,
  handleArrowNudge,
  handleRotateKey
};
